import React, { useState, useEffect, useRef } from "react";
import "../styles/Home.css";
import HomeHeader from "./HomeHeader.js";
import Footer from "./Footer.js"; // ✅ Ensure Footer is imported
import EmojiPickerComponent from "./EmojiPickerComponent.js";
import EmojiPicker from "emoji-picker-react"; // ✅ Import Emoji Picker
import GiphySearchBox from "react-giphy-searchbox";
import { Link, useNavigate, useLocation } from "react-router-dom";
import ReplyDialog from "./ReplyDialog.js";
import Post from "./Post.js"; // ✅ Ensure Post is imported
import PostDetailModal from "./PostDetailModal.js";
import EditPostModal from "./EditPostModal.js"; // ✅ NEW import for edit modal
import BoostRequestButton from "./BoostRequestButton.js";

const Home = () => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const originalPostsRef = useRef([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const postsSectionRef = useRef(null);

  // ✅ NEW: sync search term with ?search= query in URL
  const queryParams = new URLSearchParams(location.search);
  const urlSearchTerm = queryParams.get("search") || "";
  const [searchQuery, setSearchQuery] = useState(urlSearchTerm);

  useEffect(() => {
    setSearchQuery(urlSearchTerm);
  }, [urlSearchTerm]);

  const fileInputRef = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [postContent, setPostContent] = useState("");
  const postInputRef = useRef(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [selectedGif, setSelectedGif] = useState(null);
  const navigate = useNavigate();

  const scrollHomePostsToTop = () => {
    if (postsSectionRef.current) {
      postsSectionRef.current.scrollTop = 0;
    }
  };

  useEffect(() => {
    const handleResetHomeToTop = () => {
      requestAnimationFrame(() => {
        scrollHomePostsToTop();
      });

      setTimeout(() => {
        scrollHomePostsToTop();
      }, 50);
    };

    window.addEventListener("resetHomeToTop", handleResetHomeToTop);

    return () => {
      window.removeEventListener("resetHomeToTop", handleResetHomeToTop);
    };
  }, []);

  // Deep-link handoff: support /?openPost=<id> even if router/navigation strips the query.
  // We capture it from window.location on first render, store it, then open the modal once.
  const [pendingOpenPostId, setPendingOpenPostId] = useState(null);

  useEffect(() => {
    try {
      // 1) Read from URL (best case)
      const params = new URLSearchParams(window.location.search || "");
      const raw = params.get("openPost");
      const idFromUrl = raw ? Number(raw) : null;

      // 2) Or from prior sessionStorage (after login redirects, etc.)
      const idFromStoreRaw = sessionStorage.getItem("pendingOpenPostId");
      const idFromStore = idFromStoreRaw ? Number(idFromStoreRaw) : null;

      const id = (idFromUrl && Number.isFinite(idFromUrl) && idFromUrl > 0)
        ? idFromUrl
        : (idFromStore && Number.isFinite(idFromStore) && idFromStore > 0 ? idFromStore : null);

      if (!id) return;

      // Persist so redirects (like /login) don't lose it
      sessionStorage.setItem("pendingOpenPostId", String(id));
      setPendingOpenPostId(id);

      // Clean the URL so refresh doesn't keep reopening
      if (params.has("openPost")) {
        params.delete("openPost");
        const clean = window.location.pathname + (params.toString() ? "?" + params.toString() : "") + window.location.hash;
        window.history.replaceState({}, "", clean);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!pendingOpenPostId) return;

    const run = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/posts/${pendingOpenPostId}`, {
          credentials: "include",
        });

        if (!res.ok) {
          alert("Could not load that post.");
          sessionStorage.removeItem("pendingOpenPostId");
          setPendingOpenPostId(null);
          return;
        }

        const post = await res.json();

        const fullMediaUrl = post.media_url?.startsWith("http")
          ? post.media_url
          : `${process.env.REACT_APP_API_URL}${post.media_url || ""}`;

        const fullGifUrl = post.gif_url?.startsWith("http")
          ? post.gif_url
          : `${process.env.REACT_APP_API_URL}${post.gif_url || ""}`;

        const fullThumbnail = post.thumbnail?.startsWith("http")
          ? post.thumbnail
          : `${process.env.REACT_APP_API_URL}${post.thumbnail || "/images/default-thumbnail.jpg"}`;

        setSelectedPost({
          ...post,
          media_url: fullMediaUrl,
          gif_url: fullGifUrl,
          thumbnail: fullThumbnail,
        });
        setShowPostModal(true);

        // One-shot: clear after opening
        sessionStorage.removeItem("pendingOpenPostId");
        setPendingOpenPostId(null);
      } catch (e) {
        console.error("openPost deep link failed:", e);
        alert("Network error loading the post.");
        sessionStorage.removeItem("pendingOpenPostId");
        setPendingOpenPostId(null);
      }
    };

    run();
  }, [pendingOpenPostId]);

  const [showReplyDialog, setShowReplyDialog] = useState(false);
  const [replyDialogPostId, setReplyDialogPostId] = useState(null);
  const [currentReplyPost, setCurrentReplyPost] = useState(null);
  const [replyDialogMode, setReplyDialogMode] = useState("reply");
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [activeTab, setActiveTab] = useState("forYou");
  const [followingUsers, setFollowingUsers] = useState([]);
  const [postCategory, setPostCategory] = useState("General"); // ✅ Default to "General"
  const [charLimit, setCharLimit] = useState(300); // Default fallback
  const [charRemaining, setCharRemaining] = useState(300);

  // ✅ NEW: state for tracking which post is being edited
  const [editingPost, setEditingPost] = useState(null);

  // 🔔 Boost quota & request UX state
  const [boostQuota, setBoostQuota] = useState({
    allowed: 0,
    used: 0,
    remaining: 0,
    periodEnd: null,
  });
  const [boostLoading, setBoostLoading] = useState(false);
  // Store requested postIds in a Set so the 📣 button hides immediately for that post
  const [requestedThisSession, setRequestedThisSession] = useState(() => new Set());

  // 🧮 Load current user's monthly boost quota (safe: if route missing, we just skip)
  useEffect(() => {
    const loadQuota = async () => {
      if (!currentUser?.id) return;
      try {
        const r = await fetch(
          `${process.env.REACT_APP_API_URL}/boost/quota/${currentUser.id}`,
          { credentials: "include" }
        );
        // fall back quietly if not JSON / 404
        const text = await r.text();
        let data = null;
        try { data = text ? JSON.parse(text) : null; } catch {}
        if (r.ok && data && typeof data.allowed === "number") {
          setBoostQuota({
            allowed: data.allowed,
            used: data.used,
            remaining: data.remaining,
            periodEnd: data.periodEnd || null,
          });
        }
      } catch (e) {
        console.warn("⚠️ Could not fetch boost quota", e);
      }
    };
    loadQuota();
  }, [currentUser?.id]);

  // 📣 Request a boost for a post (robust messaging)
  const handleRequestBoost = async (postId) => {
    if (!currentUser?.id) return alert("Please log in.");
    if (boostLoading) return;

    setBoostLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/boost/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userId: currentUser.id, postId }),
      });

      // Always read raw first so we can see what the server actually sent
      const raw = await res.text();
      let data = null;
      try { data = raw ? JSON.parse(raw) : null; } catch {}

      // Helper to get a friendly message from the server (or fallback)
      const pickMsg = (fallback) =>
        (data && (data.message || data.error)) || (raw && raw.slice(0, 200)) || fallback;

      // Non-200 → show the server's message (not a generic "network error")
      if (!res.ok) {
        // Special handling for known codes from the backend
        const code = data?.code;
        if (code === "LIMIT_REACHED") {
          // Update quota to 0 so the 📣 button hides immediately
          setBoostQuota((q) => ({
            ...q,
            allowed: data?.quota?.allowed ?? q.allowed,
            used: data?.quota?.used ?? q.used,
            remaining: 0,
            periodEnd: data?.quota?.periodEnd ?? q.periodEnd,
          }));
        }
        return alert(`❌ ${pickMsg("Request failed")}`);
      }

      // 200 OK — success path
      alert(`✅ ${pickMsg("Boost request received.")}`);

      // If server returned quota, sync it so the 📣 button hides as needed
      if (data?.quota) {
        setBoostQuota((q) => ({
          ...q,
          allowed: data.quota.allowed ?? q.allowed,
          used: data.quota.used ?? q.used,
          remaining:
            typeof data.quota.remaining === "number"
              ? data.quota.remaining
              : Math.max(0, (q.remaining ?? 0) - 1),
          periodEnd: data.quota.periodEnd || data.quota.resetAt || q.periodEnd || null,
        }));
      }

      // Hide the 📣 for this post for this session
      setRequestedThisSession((prev) => {
        const copy = new Set(prev);
        copy.add(postId);
        return copy;
      });
    } catch (err) {
      console.error("❌ Request boost failed:", err);
      // Only show this if the fetch truly threw (network/cors)
      alert("❌ Network error contacting the boost service.");
    } finally {
      setBoostLoading(false);
    }
  };

  // ✅ FIX: Ensure we're safely accessing the alias (should match the one used in followRoutes.js)
  console.log("🧪 First following user object:", followingUsers[0]);

  const followingUserIds = followingUsers.map((u) => u.following_user_id || u.followingId || u.FollowingId || u.id).filter(Boolean);

  const handleTabChange = (tab) => {
    console.log("🔁 Switching tab to:", tab);
    setActiveTab(tab);
    setPostCategory("General");
    localStorage.removeItem("selectedCategory");
    fetchPosts();
  };

  console.log("👥 Following User IDs:", followingUserIds);

  useEffect(() => {
    if (posts.length > 0) {
      console.log("🔍 First post object JSON:", JSON.stringify(posts[0], null, 2));
    }
  }, [posts]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("currentUser"));
    if (storedUser) {
      setCurrentUser(storedUser); // ✅ Needed for Post component

      const userThumbnail = storedUser?.thumbnail?.startsWith("http")
        ? storedUser.thumbnail.replace("3001", "3000")
        : `${process.env.REACT_APP_API_URL}${storedUser?.thumbnail || "/images/default-thumbnail.jpg"}`;

      // Inject thumbnail into future updates
      window.updatePosts = (updateFunc) => {
        setPosts((prevPosts) => {
          const result = typeof updateFunc === "function" ? updateFunc(prevPosts) : updateFunc;
          return result.map((post) => ({
            ...post,
            thumbnail: post.thumbnail?.startsWith("http")
              ? post.thumbnail.replace("3001", "3000")
              : userThumbnail,
          }));
        });
      };

      // 🔵 Fetch following list (updated to log the full response)
      fetch(`${process.env.REACT_APP_API_URL}/api/following/${storedUser.id}`)
        .then((res) => res.json())
        .then((data) => {
          console.log("📦 Full /following API response:", data);

          // Store the full objects with user data for filtering and display
          setFollowingUsers(data);
        })
        .catch((err) => console.error("❌ Error fetching following IDs:", err));

    }
  }, []);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("currentUser"));
    const groupId = storedUser?.group_id;

    if (groupId) {
      fetch(`${process.env.REACT_APP_API_URL}/api/group/${groupId}`)
        .then((res) => res.json())
        .then((data) => {
          setCharLimit(data.postLimit || 500);
          setCharRemaining(data.postLimit || 500);
        })
        .catch((err) => {
          console.error("❌ Failed to fetch post limit:", err);
          setCharLimit(500);
          setCharRemaining(500);
        });
    }
  }, []);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("currentUser"));
    const groupId = storedUser?.group_id;

    if (groupId) {
      fetch(`${process.env.REACT_APP_API_URL}/api/group/${groupId}`)
        .then((res) => res.json())
        .then((data) => {
          setCharLimit(data.postLimit || 500);
          setCharRemaining(data.postLimit || 500);
        })
        .catch(() => {
          setCharLimit(500);
          setCharRemaining(500);
        });
    }
  }, []);

  useEffect(() => {
    setCharRemaining(charLimit - postContent.length);
  }, [postContent, charLimit]);

  useEffect(() => {
    const storedCategory = localStorage.getItem("selectedCategory");

    if (storedCategory) {
      console.log("🔥 useEffect triggered — selectedCategory:", storedCategory);
      setPostCategory(storedCategory);
      setActiveTab("forYou");

      fetchPosts(); // ✅ Fetches from backend
      localStorage.removeItem("selectedCategory"); // ✅ Clears filter after use
    } else {
      fetchPosts();
    }
  }, [location.key]);

  // ✅ Add handleProfileNavigation here
  const handleProfileNavigation = (username) => {
    if (username === user?.username) {
      navigate("/profile");  // ✅ Opens full profile with Edit button
    } else {
      navigate(`/profile/${username}`);  // ✅ Opens public profile view
    }
  };

  const handleGifSelect = (gif) => {
    console.log("✅ Selected GIF URL:", gif.images.original.url);

    // ✅ Store the GIF separately for preview (not in the text box)
    setSelectedGif(gif.images.original.url);

    // ✅ Close the GIF modal
    setShowGifPicker(false);
  };

  // ✅ Fetch Posts
  const fetchPosts = async (categoryOverride = null) => {
    try {
      const storedCategory = categoryOverride || localStorage.getItem("selectedCategory");

      // Grab currentUser from state or localStorage
      const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
      const userId = currentUser?.id;

      const endpoint = storedCategory
        ? `${process.env.REACT_APP_API_URL}/posts/category/${encodeURIComponent(storedCategory)}?userId=${currentUser?.id || ""}`
        : `${process.env.REACT_APP_API_URL}/posts?userId=${currentUser?.id || ""}`;

      console.log("📡 Fetching from endpoint:", endpoint);

      const response = await fetch(endpoint, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch posts.");
      let data = await response.json();

      const updatedPosts = data.map((post) => ({
        ...post,
        repliesCount: Number(post.repliesCount ?? 0),
        repostsCount: Number(post.repostsCount ?? 0),
        likes: Number(post.likes ?? 0),
        thumbnail: post.thumbnail?.startsWith("http")
          ? post.thumbnail.replace("3001", "3000")
          : `${process.env.REACT_APP_API_URL}${post.thumbnail || "/images/default-thumbnail.jpg"}`,
      }));

      // Not Interested is intentionally temporary: keep hidden posts out of
      // Home for this browser-tab session, including refetches/navigation.
      let visiblePosts = updatedPosts;
      if (currentUser?.id) {
        try {
          const hiddenKey = `scoldyHiddenPostIds:${currentUser.id}`;
          const hiddenIds = new Set(
            JSON.parse(sessionStorage.getItem(hiddenKey) || "[]").map(String)
          );
          visiblePosts = updatedPosts.filter((post) => !hiddenIds.has(String(post.id)));
        } catch (e) {
          console.warn("Could not restore Not Interested posts:", e);
        }
      }

      originalPostsRef.current = visiblePosts;
      setPosts(visiblePosts);

      if (!categoryOverride) {
        localStorage.removeItem("selectedCategory"); // ✅ Clean up if it was just used
      }

    } catch (error) {
      console.error("❌ Error fetching posts:", error);
    }
  };

  useEffect(() => {
    console.log("📌 showGifPicker state changed:", showGifPicker);
  }, [showGifPicker]);

  // Load user and fetch posts from database
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("currentUser"));

    if (storedUser) {
      setUser(prevUser => ({
        ...prevUser,
        username: storedUser.username,
        email: storedUser.email,
        thumbnail: storedUser.thumbnail?.startsWith("http")
          ? storedUser.thumbnail
          : storedUser.thumbnail?.startsWith("/images/")
            ? `${process.env.REACT_APP_PUBLIC_URL}${storedUser.thumbnail}`
            : `${process.env.REACT_APP_API_URL}/${storedUser.thumbnail}`
      }));
      console.log("✅ Loaded user from localStorage:", storedUser);
    } else {
      console.error("❌ No user found in localStorage.");
    }

    fetchPosts();

    // Keep desktop keyboard focus behavior, but never open the keyboard
    // automatically when Home is entered in a phone browser.
    const isPhoneBrowser = window.matchMedia("(max-width: 900px)").matches;

    if (!isPhoneBrowser) {
      setTimeout(() => {
        if (window.location.pathname === "/" && postInputRef.current) {
          postInputRef.current.focus();
        }
      }, 300);
    }

    // ✅ Handle profile image update event
    const handleProfileUpdate = () => {
      const refreshedUser = JSON.parse(localStorage.getItem("currentUser"));
      if (refreshedUser) {
        setUser(prevUser => ({
          ...prevUser,
          thumbnail: refreshedUser.thumbnail
            ? `${refreshedUser.thumbnail}?refresh=${Date.now()}`
            : "/images/default-thumbnail.jpg",
        }));
        console.log("🔄 Profile image updated, refreshing UI...");
      }
    };

    window.addEventListener("profileImageUpdated", handleProfileUpdate);

    return () => {
      window.removeEventListener("profileImageUpdated", handleProfileUpdate);
    };
  }, []); // ✅ Runs once when component mounts

  // HandleMediaUpload
  const handleMediaUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log("📸 Selected file:", file.name);
    setSelectedFile(file); // ✅ Store the selected file
  };

  const handleEmojiClick = (emojiData) => {
    setPostContent((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false); // Close picker after selection
  };

  // ✅ Track the last focused input field
  const lastFocusedInput = useRef(null);

  // ✅ Save the last focused input before blur
  const handleInputFocus = (event) => {
    if (event.target.id !== "searchBox") {  // ✅ Ignore search box
      lastFocusedInput.current = event.target;
    }
  };

  // ✅ Restore focus on post input if needed
  useEffect(() => {
    if (
      lastFocusedInput.current &&
      document.activeElement !== lastFocusedInput.current &&
      lastFocusedInput.current.id !== "searchBox"
    ) {
      lastFocusedInput.current.focus();  // ✅ Only refocus post input
      console.log("🔄 Refocused on last input:", lastFocusedInput.current);
    }
  }, [postContent]);

  // Add a new post
  const handlePost = async () => {
    if (!postContent.trim() && !selectedFile && !selectedGif) {
      alert("Post content, an image, or a GIF is required.");
      return;
    }
    if (!user || !user.username) {
      alert("No user logged in.");
      return;
    }

    const formData = new FormData();
    formData.append("content", postContent.trim() || "📸 Uploaded Media");
    formData.append("username", user.username);
    formData.append("category", postCategory);

    setPostContent("");
    setSelectedFile(null);
    setSelectedGif(null);
    setPostCategory("General"); // ✅ Reset to default

    // ✅ Attach media or GIF URL
    if (selectedFile) {
      formData.append("file", selectedFile);
    }
    if (selectedGif) {
      formData.append("gif", selectedGif);
    }

    console.log("📤 Form Data:", Object.fromEntries(formData.entries()));

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create post.");
      }

      const newPost = await response.json();
      console.log("📝 New Post Data from Backend:", newPost);

      // ✅ Ensure GIF appears in the post
      setPosts(prevPosts => [
        {
          ...newPost,
          media_url: newPost.media_url
            ? `${process.env.REACT_APP_API_URL}${newPost.media_url}?t=${Date.now()}`
            : selectedGif || null,  // ✅ Ensure GIF gets displayed
          thumbnail: newPost.thumbnail
            ? `${process.env.REACT_APP_API_URL}${newPost.thumbnail}?t=${Date.now()}`
            : "/images/default-thumbnail.jpg",
        },
        ...prevPosts,
      ]);

      // ✅ Clear input fields but KEEP FOCUS on input box
      setPostContent("");
      setSelectedFile(null);
      setSelectedGif(null);

      setTimeout(() => {
        if (postInputRef.current) {
          postInputRef.current.focus();  // ✅ Ensures input keeps focus
        }
      }, 100);

    } catch (error) {
      console.error("❌ Error creating post:", error);
    }
  };

  const fetchLatestPosts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts`);

      if (!response.ok) throw new Error("Failed to fetch posts.");

      let data = await response.json();

      // ✅ Merge new data with existing posts without clearing state
      setPosts(prevPosts => {
        const updatedPosts = data.map(post => ({
          ...post,
          media_url: post.media_url
            ? `${process.env.REACT_APP_API_URL}${post.media_url}?t=${Date.now()}`
            : null,
          thumbnail: post.thumbnail
            ? `${process.env.REACT_APP_API_URL}${post.thumbnail}?t=${Date.now()}`
            : "/images/default-thumbnail.jpg",
        }));

        // ✅ Prevent wiping out images by merging with prevPosts
        const mergedPosts = [...prevPosts, ...updatedPosts];

        console.log("📥 Updated posts after posting:", mergedPosts);
        return mergedPosts;
      });

    } catch (error) {
      console.error("❌ Error fetching posts:", error);
    }
  };

  // Handle search
  const handleSearch = (query) => {
    if (!query || query.trim() === "") {
      // ✅ Reset to all posts when search is cleared
      fetch(`${process.env.REACT_APP_API_URL}/posts`)
        .then((response) => response.json())
        .then((data) => {
          const updatedPosts = data.map((post) => ({
            ...post,
            thumbnail: post.thumbnail?.startsWith("http")
              ? post.thumbnail
              : `${process.env.REACT_APP_API_URL}${post.thumbnail || "/images/default-thumbnail.jpg"}`
          }));
          console.log("📥 Reset search: Reloaded posts with correct thumbnails:", updatedPosts);
          setPosts(updatedPosts);
          // ✅ Keep a copy of the original list for future searches
          originalPostsRef.current = updatedPosts;
        })
        .catch((error) => console.error("❌ Error fetching posts:", error));
      return; // ✅ Ensure early exit
    }

    const lowerQuery = query.toLowerCase().trim();

    // ✅ If query starts with '@', search for usernames ONLY
    if (lowerQuery.startsWith("@")) {
      const usernameQuery = lowerQuery.substring(1);

      if (usernameQuery.length > 1) {  // ✅ Require at least 2 characters after '@'
        const userPosts = originalPostsRef.current.filter((post) =>
          post.username.toLowerCase().includes(usernameQuery)
        );

        console.log(`🔎 Searching for posts by: @${usernameQuery}`, userPosts);

        if (userPosts.length > 0) {
          setPosts(userPosts);
        } else {
          console.log(`⚠️ No posts found for @${usernameQuery}`);
          setPosts([]); // ✅ Show empty instead of shrinking forever
        }
      }
      return;
    }

    // ✅ Default text search (searches post content normally)
    const filteredPosts = originalPostsRef.current.filter((post) =>
      post.content.toLowerCase().includes(lowerQuery)
    );

    console.log(`🔎 Searching for text: ${query}`, filteredPosts);
    setPosts(filteredPosts);
  };

  // Like a post
  const handleLike = async (postId) => {
    try {
      // Optimistically show the Like as active immediately.
      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.id === postId
            ? {
                ...post,
                likes: (post.likes || 0) + 1,
                likedByCurrentUser: 1,
              }
            : post
        )
      );

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/posts/${postId}/like`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username: user.username }),
        }
      );

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.warn("⚠️ Backend returned a non-JSON response:", text);
        fetchPosts();
        return;
      }

      const data = await response.json();
      console.log("✅ Like Response:", data);

      if (!response.ok) {
        console.error("❌ Like request failed:", data);
        fetchPosts();
        return;
      }

      // Whether this was a new Like or an already-existing Like,
      // the current user has liked this post. Fetch the authoritative count.
      const likeCountResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/posts/${postId}`,
        { credentials: "include" }
      );

      if (likeCountResponse.ok) {
        const likeData = await likeCountResponse.json();

        setPosts((prevPosts) =>
          prevPosts.map((post) =>
            post.id === postId
              ? {
                  ...post,
                  likes: Number(likeData.likes ?? post.likes ?? 0),
                  likedByCurrentUser: 1,
                }
              : post
          )
        );
      } else {
        setPosts((prevPosts) =>
          prevPosts.map((post) =>
            post.id === postId
              ? { ...post, likedByCurrentUser: 1 }
              : post
          )
        );
      }
    } catch (error) {
      console.error("❌ Error liking post:", error);
      fetchPosts();
    }
  };

  const handleNotInterested = (postId) => {
    const viewer = JSON.parse(localStorage.getItem("currentUser") || "null");
    if (!viewer?.id) return;

    try {
      const hiddenKey = `scoldyHiddenPostIds:${viewer.id}`;
      const hiddenIds = new Set(
        JSON.parse(sessionStorage.getItem(hiddenKey) || "[]").map(String)
      );
      hiddenIds.add(String(postId));
      sessionStorage.setItem(hiddenKey, JSON.stringify([...hiddenIds]));
    } catch (e) {
      console.warn("Could not save Not Interested post:", e);
    }

    originalPostsRef.current = originalPostsRef.current.filter(
      (post) => String(post.id) !== String(postId)
    );
    setPosts((prev) => prev.filter((post) => String(post.id) !== String(postId)));
  };

  const handleMuteUser = async (mutedId) => {
    const viewer = JSON.parse(localStorage.getItem("currentUser") || "null");
    if (!viewer?.id || !mutedId) return;

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/mute/${mutedId}?mutedByUserId=${viewer.id}`,
      { method: "POST", credentials: "include" }
    );

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      if (response.status !== 400 || !String(data.error || "").toLowerCase().includes("already muted")) {
        alert(data.error || "Unable to mute this user.");
        return;
      }
    }

    originalPostsRef.current = originalPostsRef.current.filter(
      (post) => Number(post.user_id) !== Number(mutedId)
    );
    setPosts((prev) => prev.filter((post) => Number(post.user_id) !== Number(mutedId)));
  };

  const handleBlockUser = async (blockedId, post) => {
    const viewer = JSON.parse(localStorage.getItem("currentUser") || "null");
    if (!viewer?.id || !blockedId) return;

    const username = post?.username ? `@${post.username}` : "this user";
    if (!window.confirm(`Block ${username}? Their posts will be hidden from your Home feed.`)) return;

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/users/${blockedId}/block`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ blockerId: viewer.id }),
      }
    );

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      if (response.status !== 400 || !String(data.error || "").toLowerCase().includes("already blocked")) {
        alert(data.error || "Unable to block this user.");
        return;
      }
    }

    originalPostsRef.current = originalPostsRef.current.filter(
      (item) => Number(item.user_id) !== Number(blockedId)
    );
    setPosts((prev) => prev.filter((item) => Number(item.user_id) !== Number(blockedId)));
  };

  const handleReport = async (postId, reportData) => {
    const viewer = JSON.parse(localStorage.getItem("currentUser") || "null");
    const details =
      typeof reportData === "string" ? reportData : reportData?.content || "";

    if (!viewer?.id || !details.trim()) return;

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/report/${postId}?reporter_id=${viewer.id}`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason: "Reported by user",
          details: details.trim(),
        }),
      }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      alert(data.error || "Unable to submit this report.");
      throw new Error(data.error || "Unable to submit this report.");
    }

    alert("Report submitted for moderation.");
  };

  const handleReply = async (postId, replyData) => {
    const content =
      typeof replyData === "string" ? replyData : replyData?.content || "";
    const file =
      typeof replyData === "object" ? replyData?.file || null : null;
    const gif =
      typeof replyData === "object" ? replyData?.gif || null : null;

    if (!content.trim() && !file && !gif) return;

    try {
      const formData = new FormData();
      formData.append("content", content);
      formData.append("username", user.username);

      if (file) formData.append("file", file);
      if (gif) formData.append("gif", gif);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/posts/${postId}/reply`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to reply to post.");
      }

      console.log(`✅ Replied to post ${postId}`);
      await fetchPosts();
    } catch (error) {
      console.error("❌ Error replying to post:", error);
      throw error;
    }
  };

  // ✅ Open Reply Dialog Instead of Prompt
  const openReplyDialog = (postId) => {
    console.log(`🗨️ Open reply dialog for post ${postId}`);

    if (!posts || posts.length === 0) {
      console.error("❌ No posts loaded yet. Cannot open reply dialog.");
      return;
    }

    const postToReply = posts.find(post => post.id === postId);

    if (!postToReply) {
      console.error(`❌ Post not found for ID: ${postId}`, posts);
      return;
    }

    console.log("✅ Found post for reply:", postToReply);

    setReplyDialogMode("reply");
    setCurrentReplyPost(postToReply); // ✅ Store the selected post
    setReplyDialogPostId(postId); // ✅ Ensure we track the post ID
    setShowReplyDialog(true); // ✅ Open the reply dialog
  };

  const openReportDialog = (postId) => {
    if (!posts || posts.length === 0) return;
    const postToReport = posts.find((post) => String(post.id) === String(postId));
    if (!postToReport) return;

    setReplyDialogMode("report");
    setCurrentReplyPost(postToReport);
    setReplyDialogPostId(postId);
    setShowReplyDialog(true);
  };

  // Repost a post
  const handleRepost = async (postId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/${postId}/repost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: user.username }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to repost.");
      }

      const newRepost = await response.json();
      console.log(`✅ Reposted post ${postId}:`, newRepost);

      // ✅ Update UI with the new repost
      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === postId
            ? {
                ...post,
                repostsCount: (post.repostsCount || 0) + 1,
                repostedByCurrentUser: 1,
              }
            : post
        )
      );

    } catch (error) {
      console.error("❌ Error reposting:", error);
    }
  };

  // Delete a post
  const handleDelete = async (postId, user) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/${postId}?username=${encodeURIComponent(user.username)}&group_id=${user.group_id}`, {
        method: "DELETE",
      });

      if (!response.ok) throw new Error("Failed to delete post.");

      setPosts(prevPosts => prevPosts.filter(p => p.id !== postId));
    } catch (error) {
      console.error("❌ Error deleting post:", error);
    }
  };

  // Dropdown toggle
  const [activeDropdown, setActiveDropdown] = useState(null);

  const toggleDropdown = (postId) => {
    setActiveDropdown(activeDropdown === postId ? null : postId);
  };

  // Close dropdown on clicking anywhere else
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // ✅ Debugging GIF Picker Visibility
  useEffect(() => {
    console.log("📌 showGifPicker state changed:", showGifPicker);
  }, [showGifPicker]);

  return (
    <div className="home-container">
      <HomeHeader
        searchQuery={searchQuery}               // ← comes from useState in Home.js
        onSearchChange={setSearchQuery}         // ← updates searchQuery in Home.js
        activeTab={activeTab}
        handleTabChange={setActiveTab}
      />

      {showGifPicker && (
        <div
          className="gif-modal"
          style={{
            position: "absolute",
            bottom: "-80px",  // ✅ Move it lower so the input text is fully visible
            left: "10px",
            zIndex: 1000,
            background: "white",
            padding: "15px",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)"
          }}
        >
          <button
            className="close-btn"
            onClick={() => setShowGifPicker(false)}
            style={{ display: "block", marginBottom: "10px", cursor: "pointer" }}
          >
            ✖ Close
          </button>

          <GiphySearchBox
            apiKey="4OQsubW6vV4uAMHoGaiK8VRRavmxEvYW"
            onSelect={handleGifSelect} // ✅ Inserts GIF into post input box
          />
        </div>
      )}

      <section className="posting-summary">
        {user && (
          <>
            {/* ✅ Ensure Thumbnail & Input Box Align */}
            <div className="post-input-container">
              <img
                src={
                  user?.thumbnail?.startsWith("http")
                    ? user.thumbnail
                    : `${process.env.REACT_APP_API_URL}/${user?.thumbnail}`
                }
                alt="User Thumbnail"
                className="summary-thumbnail"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/images/default-thumbnail.jpg";
                }}
              />
              <div className="post-input-content" style={{ width: "100%", maxWidth: "600px" }}>

                {/* 📝 Post Input */}
                <div style={{ position: "relative", width: "100%", maxWidth: "600px" }}>
                  <textarea
                    ref={postInputRef}
                    id="newPostContent"
                    className="post-input"
                    placeholder="What's on your mind?"
                    value={postContent}
                    onChange={(e) => {
                      setPostContent(e.target.value);
                      setCharRemaining(charLimit - e.target.value.length);
                    }}
                    onFocus={handleInputFocus}
                    style={{
                      width: "100%",
                      minHeight: "80px",
                      paddingBottom: "24px"  // Make room for the counter inside the box
                    }}
                  ></textarea>

                  <div
                    className="char-counter"
                    style={{
                      position: "absolute",
                      bottom: "6px",
                      right: "10px",
                      fontSize: "12px",
                      color: charRemaining <= 10 ? "red" : "#666",
                      pointerEvents: "none",
                      background: "white"
                    }}
                  >
                    {charRemaining} left
                  </div>
                </div>

                {charRemaining <= 30 && (
                  <div
                    className="char-counter"
                    style={{
                      fontSize: "12px",
                      textAlign: "right",
                      color: charRemaining <= 10 ? "red" : "#666",
                      marginTop: "4px"
                    }}
                  >
                    {charRemaining} characters remaining
                  </div>
                )}

                {/* 🔵 Category Selector */}
                <div className="category-dropdown-wrapper" style={{ marginBottom: "10px" }}>
                  <label htmlFor="categorySelect" style={{ marginRight: "8px" }}>#Feed:</label>
                  <select
                    id="categorySelect"
                    value={postCategory}
                    onChange={(e) => setPostCategory(e.target.value)}
                    className="category-select"
                    style={{
                      padding: "6px",
                      borderRadius: "6px",
                      width: "100%",
                      maxWidth: "300px"
                    }}
                  >
                    <option value="General">General (Default)</option>
                    <option value="AI">AI</option>
                    <option value="Crypto">Crypto</option>
                    <option value="Economy">Economy</option>
                    <option value="Entertainment">Entertainment</option>
                    <option value="Finance">Finance</option>
                    <option value="Health">Health</option>
                    <option value="Media">Media</option>
                    <option value="News">News</option>
                    <option value="Politics">Politics</option>
                    <option value="Science">Science</option>
                    <option value="Sports">Sports</option>
                    <option value="Technology">Technology</option>
                    <option value="Travel">Travel</option>
                    <option value="Weather">Weather</option>
                    <option value="World">World</option>
</select>
                </div>

                {/* ✅ GIF Preview (No changes here) */}
                {selectedGif && (
                  <div className="gif-preview" style={{ textAlign: "center", marginTop: "5px" }}>
                    <img
                      src={selectedGif}
                      alt="Selected GIF"
                      style={{ maxWidth: "80%", maxHeight: "150px", borderRadius: "8px", marginBottom: "5px" }}
                    />
                    <button
                      className="remove-gif-btn"
                      onClick={() => setSelectedGif(null)}
                      style={{ display: "block", marginTop: "5px", cursor: "pointer" }}
                    >
                      ✖ Remove GIF
                    </button>
                  </div>
                )}

                {/* ✅ Unified Action Bar + Post/Cancel Row */}
                {/* ✅ Aligned Post Action Row */}
                <div style={{ width: "100%", maxWidth: "600px", margin: "0 auto" }}>
                  <div className="post-actions-row">
                    {/* 🔵 Left: Action Icons */}
                    <div className="action-buttons">
                      <button className="action-btn" data-tooltip="Upload Media" onClick={() => fileInputRef.current.click()}>
                        📷
                      </button>
                      <button className="action-btn" data-tooltip="Add GIF" onClick={() => setShowGifPicker(true)}>
                        🎞
                      </button>
                      <button className="action-btn" data-tooltip="Insert Emoji" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                        😊
                      </button>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept="image/*,video/*"
                        onChange={handleMediaUpload}
                      />
                    </div>

                    {/* 🔵 Right: Cancel + Post */}
                    <div className="post-buttons-right">
                      <button
                        className="cancel-post-button"
                        title="Cancel Post"
                        onClick={() => {
                          setPostContent("");
                          setSelectedGif(null);
                          setPostCategory("General");
                        }}
                      >
                        ↩
                      </button>
                      <button
                        className="post-button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handlePost();
                          setTimeout(() => postInputRef.current?.focus(), 10);
                        }}
                      >
                        Post
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ✅ Show Emoji Picker Below the Action Bar */}
            {showEmojiPicker && (
              <div className="emoji-picker-container">
                <EmojiPicker onEmojiClick={handleEmojiClick} />
              </div>
            )}
          </>
        )}
      </section>

      <section className="posts-section" ref={postsSectionRef}>
        <h2>{activeTab === "forYou" ? "All Posts" : "Following"}</h2>
        <div className="posts-list">
          {posts.length > 0 ? (() => {
            // ✅ Base filter (For You vs Following)
            let filteredPosts = posts.filter((post) =>
              activeTab === "forYou" ? true : followingUserIds.includes(post.user_id)
            );

            // ✅ Apply search filter
            if (searchQuery) {
              if (searchQuery.startsWith("@")) {
                const username = searchQuery.slice(1).toLowerCase();
                filteredPosts = filteredPosts.filter((post) =>
                  post.username?.toLowerCase().includes(username)
                );
              } else {
                const term = searchQuery.toLowerCase();
                filteredPosts = filteredPosts.filter(
                  (post) =>
                    post.content?.toLowerCase().includes(term) ||
                    post.username?.toLowerCase().includes(term)
                );
              }
            }

            const viewer = currentUser || user;

            // ✅ Admin-boosted post (only one allowed)
            const adminBoostPost = filteredPosts.find((post) => post.isAdminBoosted);

            // ✅ Up to 3 user-boosted posts (excluding admin post)
            const userBoostedPosts = filteredPosts
              .filter((post) => post.isBoosted && !post.isAdminBoosted)
              .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
              .slice(0, 3);

            // ✅ Combine boosted posts (admin goes first)
            const finalBoostedPosts = adminBoostPost
              ? [adminBoostPost, ...userBoostedPosts]
              : userBoostedPosts;

            const boostedPostIds = finalBoostedPosts.map((post) => post.id);

            // ✅ Remaining regular posts
            const regularPosts = filteredPosts
              .filter((post) => !boostedPostIds.includes(post.id))
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            // ✅ Determine if post is editable
            const canEditPost = (currentUser, post) => {
              if (!currentUser || !post) return false;

              const minutesPassed = (Date.now() - new Date(post.createdAt)) / 60000;
              const isOwner = currentUser.id === post.user_id;

              if (currentUser.group_id === 1) return isOwner;
              if (currentUser.group_id >= 2 && currentUser.group_id <= 4) return isOwner && minutesPassed <= 60;
              if (currentUser.group_id === 6) return isOwner && minutesPassed <= 30;
              if (currentUser.group_id === 7) return isOwner && minutesPassed <= 45;
              if (currentUser.group_id === 8) return isOwner && minutesPassed <= 60;
              if (currentUser.group_id === 9) return isOwner;

              return false;
            };

            // ✅ Shared Boost Request Handler (works for ALL posts)
            const requestBoostHandler = async (postId, btn) => {
              try {
                const uid = viewer?.id;
                if (!uid) { alert("Please log in."); return; }

                btn.disabled = true;
                btn.textContent = "⏳";

                const res = await fetch(`${process.env.REACT_APP_API_URL}/boost/request`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({ userId: uid, postId }),
                });

                const data = await res.json().catch(() => ({}));

                if (res.ok) {
                  btn.textContent = "Requested";
                } else if (res.status === 409) {
                  btn.textContent = "Already Requested";
                } else if (res.status === 429) {
                  btn.textContent = "Limit Reached";
                } else {
                  btn.disabled = false;
                  btn.textContent = "📣";
                  alert(data?.message || "Request failed.");
                }
              } catch {
                btn.disabled = false;
                btn.textContent = "📣";
                alert("Network error.");
              }
            };

            // ✅ Render posts
            return (
              <>
                {/* 🔰 Boosted Posts */}
                {finalBoostedPosts.map((post) => (
                  <div key={`boost-${post.id}`} className="boosted-post-container">
                    <h4 className="boosted-label">
                      📢 {post.isAdminBoosted ? "Admin Boosted Post" : "Boosted Post"}
                    </h4>

                    <Post
                      post={post}
                      currentUser={currentUser}
                      onDelete={handleDelete}
                      onLike={handleLike}
                      onReply={openReplyDialog}
                      onReport={openReportDialog}
                      onNotInterested={handleNotInterested}
                      onMuteUser={handleMuteUser}
                      onBlockUser={handleBlockUser}
                      onRepost={handleRepost}
                      onNavigateToProfile={handleProfileNavigation}
                      onShowModal={(post) => {
                        const fullMediaUrl = post.media_url?.startsWith("http")
                          ? post.media_url
                          : `${process.env.REACT_APP_API_URL}${post.media_url || ""}`;
                        const fullGifUrl = post.gif_url?.startsWith("http")
                          ? post.gif_url
                          : `${process.env.REACT_APP_API_URL}${post.gif_url || ""}`;
                        const fullThumbnail = post.thumbnail?.startsWith("http")
                          ? post.thumbnail
                          : `${process.env.REACT_APP_API_URL}${post.thumbnail || "/images/default-thumbnail.jpg"}`;

                        const normalizedPost = {
                          ...post,
                          media_url: fullMediaUrl,
                          gif_url: fullGifUrl,
                          thumbnail: fullThumbnail,
                        };

                        setSelectedPost(normalizedPost);
                        setShowPostModal(true);
                      }}
                      extraActions={
                        <>
                          {canEditPost(currentUser, post) && (
                            <button
                              className="action-btn"
                              title="Edit Post"
                              onClick={() => setEditingPost(post)}
                            >
                              ✏️
                            </button>
                          )}
                          {viewer?.id === post.user_id &&
                            [6, 7, 8, 9].includes(viewer.group_id) &&
                            !post.isBoosted &&
                            !post.isAdminBoosted && (
                              <button
                                className="action-btn"
                                title="Request Boost"
                                onClick={(e) => requestBoostHandler(post.id, e.currentTarget)}
                                style={{ marginLeft: 8 }}
                              >
                                📣
                              </button>
                            )}
                        </>
                      }
                    />
                  </div>
                ))}

                {/* 🗂️ Regular Posts */}
                {regularPosts.map((post) => {
                  const canEdit = canEditPost(currentUser, post);
                  const viewerIsOwner = viewer?.id === post.user_id;
                  const viewerTier = Number(viewer?.group_id);
                  const viewerIsSubscribed = [6, 7, 8, 9].includes(viewerTier);
                  const canRequestBoost = viewerIsOwner && viewerIsSubscribed && !post.isBoosted && !post.isAdminBoosted;

                  return (
                    <Post
                      key={post.id}
                      post={post}
                      currentUser={currentUser}
                      onDelete={handleDelete}
                      onLike={handleLike}
                      onReply={openReplyDialog}
                      onReport={openReportDialog}
                      onNotInterested={handleNotInterested}
                      onMuteUser={handleMuteUser}
                      onBlockUser={handleBlockUser}
                      onRepost={handleRepost}
                      onNavigateToProfile={handleProfileNavigation}
                      onShowModal={(post) => {
                        const fullMediaUrl = post.media_url?.startsWith("http")
                          ? post.media_url
                          : `${process.env.REACT_APP_API_URL}${post.media_url || ""}`;
                        const fullGifUrl = post.gif_url?.startsWith("http")
                          ? post.gif_url
                          : `${process.env.REACT_APP_API_URL}${post.gif_url || ""}`;
                        const fullThumbnail = post.thumbnail?.startsWith("http")
                          ? post.thumbnail
                          : `${process.env.REACT_APP_API_URL}${post.thumbnail || "/images/default-thumbnail.jpg"}`;

                        const normalizedPost = {
                          ...post,
                          media_url: fullMediaUrl,
                          gif_url: fullGifUrl,
                          thumbnail: fullThumbnail,
                        };

                        setSelectedPost(normalizedPost);
                        setShowPostModal(true);
                      }}
                      extraActions={
                        <>
                          {canRequestBoost && (
                            <button
                              className="action-btn"
                              title="Request Boost"
                              onClick={(e) => requestBoostHandler(post.id, e.currentTarget)}
                              style={{ marginRight: 6 }}
                            >
                              🚀
                            </button>
                          )}
                          {canEdit && (
                            <button
                              className="action-btn"
                              title="Edit Post"
                              onClick={() => setEditingPost(post)}
                            >
                              ✏️
                            </button>
                          )}
                        </>
                      }
                    />
                  );
                })}
              </>
            );
          })() : (
            <p>No posts to display.</p>
          )}
        </div>
      </section>

      {/* ✅ Renders EditPostModal if editingPost is set */}
      {editingPost && (
        <EditPostModal
          post={editingPost}
          onClose={() => setEditingPost(null)}
          onSave={(updatedPost) => {
            // ✅ Update the edited post in the UI instantly
            setPosts((prev) =>
              prev.map((p) => (p.id === updatedPost.id ? updatedPost : p))
            );
            setEditingPost(null);
          }}
        />
      )}

      {/* ✅ Show Reply Dialog */}
      {showReplyDialog && currentReplyPost && (
        <ReplyDialog
          isOpen={showReplyDialog}
          onClose={() => setShowReplyDialog(false)}
          post={currentReplyPost}
          mode={replyDialogMode}
          onSubmit={replyDialogMode === "report" ? handleReport : handleReply}
        />
      )}

      {/* ✅ Show Post Detail Modal */}
      {showPostModal && selectedPost && (
        <PostDetailModal
          post={selectedPost}
          onClose={() => {
            console.log("🧵 Closing PostDetailModal");
            setShowPostModal(false);
            setSelectedPost(null);
          }}
          onReply={(postOrId) => {
            const postToUse =
              typeof postOrId === "object"
                ? postOrId
                : posts.find((p) => p.id === postOrId);

            if (!postToUse) {
              console.error("⚠️ Cannot find post for reply:", postOrId);
              return;
            }

            console.log("💬 Reply button clicked for post:", postToUse);

            setShowPostModal(false);
            setSelectedPost(null);

            setTimeout(() => {
              console.log("✅ Opening ReplyDialog for post:", postToUse);
              setCurrentReplyPost(postToUse);
              setReplyDialogPostId(postToUse.id);
              setShowReplyDialog(true);
            }, 100);
          }}
          onLike={handleLike}
          onRepost={handleRepost}
          onNavigateToProfile={handleProfileNavigation}
        />
      )}

      {/* ✅ End of home-container */}
    </div>
  );
};

export default Home;